import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { rollReviewPricingRepository } from '../repositories/rollReviewPricingRepository';
import {
  buildAutoTiers,
  calcAutoPrice,
  ROLL_REVIEW_TIERS,
  STRIPE_MINIMUMS,
  recalcAutoTiers,
  type PricingTier,
  type RollReviewCurrency,
  type RollReviewPricing,
  type TierKey,
} from '../domain/rollReviewPricing';

// ─── Query keys ───────────────────────────────────────────────────────────────

const PRICING_QUERY_KEY  = (userId: string) => ['roll-review-pricing', userId];
const AGENDA_QUERY_KEY   = (userId: string) => ['agenda-config-for-pricing', userId];

// ─── Validation ───────────────────────────────────────────────────────────────

export type ValidationErrors = Partial<Record<TierKey, string>>;

function validateTiers(
  tiers: PricingTier[],
  currency: RollReviewCurrency,
): ValidationErrors {
  const errors: ValidationErrors = {};
  const min = STRIPE_MINIMUMS[currency];

  for (const tier of tiers) {
    if (tier.priceCents > 0 && tier.priceCents < min) {
      errors[tier.key] = `min_${currency.toLowerCase()}`;
    }
  }
  return errors;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseRollReviewPricingReturn {
  // Data
  draft:             RollReviewPricing | null;
  savedUpdatedAt:    string | null;
  isFirstAccess:     boolean;

  // Loading / error states
  isLoading:         boolean;
  isSaving:          boolean;
  loadError:         string | null;
  saveError:         string | null;

  // Derived
  hasUnsavedChanges: boolean;
  validationErrors:  ValidationErrors;

  // Draft mutations
  setCurrency:       (c: RollReviewCurrency) => void;
  setUseHourlyBase:  (on: boolean) => void;
  setHourlyRate:     (cents: number | null) => void;
  setTierPrice:      (key: TierKey, cents: number) => void;
  resetTierToAuto:   (key: TierKey) => void;

  // Persistence
  save:    () => void;
  discard: () => void;
}

export function useRollReviewPricing(): UseRollReviewPricingReturn {
  const { user }      = useAuth();
  const queryClient   = useQueryClient();
  const userId        = user?.id ?? '';

  // ── Fetch saved pricing row ──────────────────────────────────────────────
  const {
    data:    savedPricing,
    isLoading: pricingLoading,
    error:   pricingError,
  } = useQuery({
    queryKey: PRICING_QUERY_KEY(userId),
    queryFn:  () => rollReviewPricingRepository.fetchByInstructorId(userId),
    enabled:  !!userId,
  });

  // ── Fetch agenda config (for default hourly rate) ────────────────────────
  const { data: agendaConfig, isLoading: agendaLoading } = useQuery({
    queryKey: AGENDA_QUERY_KEY(userId),
    queryFn:  async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('instructor_agenda_config')
        .select('class_value, currency')
        .eq('instructor_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data as { class_value: number | null; currency: string | null } | null;
    },
    enabled: !!userId,
  });

  const isLoading = pricingLoading || agendaLoading;

  // ── Initialise draft ─────────────────────────────────────────────────────
  const [draft, setDraft]                       = useState<RollReviewPricing | null>(null);
  const [savedUpdatedAt, setSavedUpdatedAt]     = useState<string | null>(null);
  const [saveError, setSaveError]               = useState<string | null>(null);

  useEffect(() => {
    // Wait for both queries to resolve before initialising
    if (isLoading) return;

    if (savedPricing) {
      setDraft(savedPricing);
      setSavedUpdatedAt(savedPricing.updatedAt);
      return;
    }

    // First access — build defaults from agenda config
    const agendaCurrency = (agendaConfig?.currency as RollReviewCurrency | null) ?? 'USD';
    const agendaRateCents = agendaConfig?.class_value
      ? Math.round(agendaConfig.class_value * 100)
      : null;

    const defaultHourly = agendaRateCents ?? 0;
    const defaultTiers  = defaultHourly > 0
      ? buildAutoTiers(defaultHourly)
      : ROLL_REVIEW_TIERS.map(t => ({
          key:           t.key,
          rangeStartSec: t.startSec,
          rangeEndSec:   t.endSec,
          label:         t.label,
          priceCents:    0,
          isManual:      false,
        }));

    setDraft({
      instructorId:    userId,
      currency:        agendaCurrency,
      useHourlyBase:   defaultHourly > 0,
      hourlyRateCents: defaultHourly > 0 ? defaultHourly : null,
      tiers:           defaultTiers,
      updatedAt:       null,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, savedPricing, agendaConfig]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const isFirstAccess = savedPricing === null && !isLoading;

  const hasUnsavedChanges = useMemo(() => {
    if (!draft) return false;
    if (!savedPricing) return true; // first access always has pending state

    return (
      draft.currency        !== savedPricing.currency       ||
      draft.useHourlyBase   !== savedPricing.useHourlyBase  ||
      draft.hourlyRateCents !== savedPricing.hourlyRateCents ||
      draft.tiers.some((t, i) => {
        const s = savedPricing.tiers[i];
        return t.priceCents !== s?.priceCents || t.isManual !== s?.isManual;
      })
    );
  }, [draft, savedPricing]);

  const validationErrors = useMemo(
    () => (draft ? validateTiers(draft.tiers, draft.currency) : {}),
    [draft],
  );

  // ── Draft mutations ──────────────────────────────────────────────────────

  const setCurrency = useCallback((currency: RollReviewCurrency) => {
    setDraft(d => d ? { ...d, currency } : d);
  }, []);

  const setUseHourlyBase = useCallback((useHourlyBase: boolean) => {
    setDraft(d => {
      if (!d) return d;
      if (!useHourlyBase) {
        // Switch to manual: preserve current prices, mark all as manual
        const tiers = d.tiers.map(t => ({ ...t, isManual: true }));
        return { ...d, useHourlyBase: false, tiers };
      }
      // Switch back to hourly: recalc auto tiers
      const rate  = d.hourlyRateCents ?? 0;
      const tiers = rate > 0 ? recalcAutoTiers(d.tiers, rate) : d.tiers;
      return { ...d, useHourlyBase: true, tiers };
    });
  }, []);

  const setHourlyRate = useCallback((cents: number | null) => {
    setDraft(d => {
      if (!d) return d;
      const tiers = d.useHourlyBase && cents
        ? recalcAutoTiers(d.tiers, cents)
        : d.tiers;
      return { ...d, hourlyRateCents: cents, tiers };
    });
  }, []);

  const setTierPrice = useCallback((key: TierKey, priceCents: number) => {
    setDraft(d => {
      if (!d) return d;
      const tiers = d.tiers.map(t =>
        t.key === key ? { ...t, priceCents, isManual: true } : t,
      );
      return { ...d, tiers };
    });
  }, []);

  const resetTierToAuto = useCallback((key: TierKey) => {
    setDraft(d => {
      if (!d) return d;
      const tierDef = ROLL_REVIEW_TIERS.find(t => t.key === key);
      if (!tierDef) return d;
      const priceCents = d.hourlyRateCents
        ? calcAutoPrice(tierDef.endSec, d.hourlyRateCents)
        : 0;
      const tiers = d.tiers.map(t =>
        t.key === key ? { ...t, priceCents, isManual: false } : t,
      );
      return { ...d, tiers };
    });
  }, []);

  // ── Persistence ──────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('No draft to save');

      // Conflict check: if saved row has been updated by another session
      if (savedUpdatedAt && savedPricing?.updatedAt && savedUpdatedAt !== savedPricing.updatedAt) {
        throw new Error('conflict');
      }

      return rollReviewPricingRepository.upsert(draft);
    },
    onSuccess: (persisted) => {
      setSavedUpdatedAt(persisted.updatedAt);
      setSaveError(null);
      queryClient.setQueryData(PRICING_QUERY_KEY(userId), persisted);
      queryClient.invalidateQueries({ queryKey: PRICING_QUERY_KEY(userId) });
    },
    onError: (err: Error) => {
      setSaveError(err.message);
    },
  });

  const discard = useCallback(() => {
    if (savedPricing) {
      setDraft(savedPricing);
    } else {
      // First access — reset to blank defaults
      const agendaCurrency = (agendaConfig?.currency as RollReviewCurrency | null) ?? 'USD';
      const agendaRateCents = agendaConfig?.class_value
        ? Math.round(agendaConfig.class_value * 100)
        : null;
      setDraft({
        instructorId:    userId,
        currency:        agendaCurrency,
        useHourlyBase:   !!agendaRateCents,
        hourlyRateCents: agendaRateCents,
        tiers:           agendaRateCents ? buildAutoTiers(agendaRateCents) : [],
        updatedAt:       null,
      });
    }
    setSaveError(null);
  }, [savedPricing, agendaConfig, userId]);

  return {
    draft,
    savedUpdatedAt,
    isFirstAccess,
    isLoading,
    isSaving:          saveMutation.isPending,
    loadError:         pricingError ? String(pricingError) : null,
    saveError,
    hasUnsavedChanges,
    validationErrors,
    setCurrency,
    setUseHourlyBase,
    setHourlyRate,
    setTierPrice,
    resetTierToAuto,
    save:    saveMutation.mutate,
    discard,
  };
}
